<!-- src/views/MainView.vue -->
<template>
  <v-app>
    <v-main class="main-content">
      <top-bar @search="searchFiles" />
      <v-container fluid class="content-container">
        <v-row no-gutters>
          <!-- File tree column -->
          <v-col :cols="isPreviewing ? 6 : 12">
            <v-card class="file-tree-card">
              <loading-spinner v-if="loading" />
              <file-folder
                v-else
                :contents="filteredFiles"
                folder=""
                @downloadFolder="handleDownloadFolder"
                @downloadFile="handleDownloadFile"
                @previewFile="handlePreviewFile"
              />
            </v-card>
          </v-col>

          <!-- Preview popup column -->
          <v-col v-if="isPreviewing" :cols="6">
            <file-viewer
              :preview-key="previewKey"
              :preview-type="previewType"
              :preview-pdf-path="previewPdfPath"
              :preview-docx-buffer="previewDocxBuffer"
              :preview-pptx-buffer="previewPptxBuffer"
              @download="downloadPreview"
              @close="closePreview"
            />
          </v-col>
        </v-row>
      </v-container>

      <download-notifications
        v-if="showNotifications"
        :notifications="notifications"
      />
    </v-main>
  </v-app>
</template>

<script>
import FileFolder from "../components/FileFolder.vue";
import TopBar from "../components/TopBar.vue";
import LoadingSpinner from "../components/LoadingSpinner.vue";
import DownloadNotifications from "../components/DownloadNotifications.vue";
import FileViewer from "./FileViewer.vue";
import {
  listFiles,
  downloadFile,
  downloadFolder,
  getPdfFileUrl,
  getFileUrl,
} from "@/awsService";

export default {
  name: "MainView",
  components: {
    FileFolder,
    TopBar,
    LoadingSpinner,
    DownloadNotifications,
    FileViewer,
  },
  props: {
    bucketName: { type: String, required: true },
    version: { type: String, required: true },
  },
  data() {
    return {
      files: {},
      searchQuery: "",
      loading: true,
      notifications: [],
      showNotifications: false,

      previewKey: "",
      previewType: "",         // 'pdf' | 'docx' | 'pptx'
      previewPdfPath: "",
      previewDocxBuffer: null, // ArrayBuffer
      previewPptxBuffer: null, // ArrayBuffer
    };
  },
  computed: {
    filteredFiles() {
      if (!this.searchQuery) return this.files;
      const recurse = (contents) => {
        const out = {};
        for (const [name, item] of Object.entries(contents)) {
          if (item.lastModified == null) {
            const nested = recurse(item);
            if (Object.keys(nested).length) out[name] = nested;
          } else if (
            name.toLowerCase().includes(this.searchQuery.toLowerCase())
          ) {
            out[name] = item;
          }
        }
        return out;
      };
      return recurse(this.files);
    },
    isPreviewing() {
      return (
        this.previewType === "pdf" ||
        this.previewType === "docx" ||
        this.previewType === "pptx"
      );
    },
  },
  async created() {
    this.files = await listFiles(this.bucketName, this.version);
    this.loading = false;
  },
  methods: {
    async handleDownloadFile(key) {
      if (!this.showNotifications) this.showNotifications = true;
      const id = `${this.version}/${key}-${Date.now()}`;
      this.notifications.push({ id, key, completed: false });
      await downloadFile(this.bucketName, `${this.version}/${key}`);
      const note = this.notifications.find((n) => n.id === id);
      if (note) note.completed = true;
    },
    async handleDownloadFolder(key) {
      if (!this.showNotifications) this.showNotifications = true;
      const id = `${key}-${Date.now()}`;
      this.notifications.push({ id, key, completed: false });
      await downloadFolder(this.bucketName, `${this.version}/${key}`);
      const note = this.notifications.find((n) => n.id === id);
      if (note) note.completed = true;
    },
    async handlePreviewFile(key) {
      this.previewKey = key;
      const lower = key.toLowerCase();
      if (lower.endsWith(".pdf")) {
        this.previewType = "pdf";
        this.previewPdfPath = await getPdfFileUrl(
          this.bucketName,
          `${this.version}/${key}`
        );
        this.previewDocxBuffer = null;
        this.previewPptxBuffer = null;
      } else if (lower.endsWith(".docx")) {
        this.previewType = "docx";
        this.previewPdfPath = "";
        this.previewPptxBuffer = null;
        const url = await getFileUrl(
          this.bucketName,
          `${this.version}/${key}`
        );
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        this.previewDocxBuffer = buffer;
      } else if (lower.endsWith(".pptx")) {
        this.previewType = "pptx";
        this.previewPdfPath = "";
        this.previewDocxBuffer = null;
        const url = await getFileUrl(
          this.bucketName,
          `${this.version}/${key}`
        );
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        this.previewPptxBuffer = buffer;
      } else {
        this.previewType = "";
      }
    },
    downloadPreview() {
      if (this.previewKey) {
        this.handleDownloadFile(this.previewKey);
      }
    },
    closePreview() {
      this.previewKey = "";
      this.previewType = "";
      this.previewPdfPath = "";
      this.previewDocxBuffer = null;
      this.previewPptxBuffer = null;
    },
    searchFiles(q) {
      this.searchQuery = q;
    },
  },
};
</script>

<style scoped>
.main-content {
  height: 100vh;
}
.file-tree-card {
  height: 100%;
  overflow: auto;
}


</style>
